/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/
import {
  AbsoluteTime,
  ChallengerApi,
  EmptyObject,
  HttpStatusCode,
  TalerError,
  TalerFormAttributes,
  TranslatedString,
} from "@gnu-taler/taler-util";
import {
  RouteDefinition,
  useAsyncAction,
  useChallengerApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  revalidateChallengeSession,
  useChallengeSession,
} from "../hooks/challenge.js";
import { SessionId, useSessionState } from "../hooks/session.js";
import {
  ActionButton,
  formatErrorDetails,
  formatHttpErrorDetails,
  InlineNotice,
  LoadingState,
  PrimaryActionLink,
  SecondaryAction,
  SecondaryButton,
  TechnicalDetails,
  TextActionLink,
  VerificationCard,
} from "../components/VerificationUi.js";
import { challengeFailureMessage } from "./AskChallenge.js";

type Props = {
  focus?: boolean;
  session: SessionId;
  onComplete: () => void;
  routeAsk: RouteDefinition<EmptyObject>;
};

export function getAddressDescriptionFromAddrType(
  type: ChallengerApi.ChallengerTermsOfServiceResponse["address_type"],
  addr: Record<string, string>,
): string {
  switch (type) {
    case "email":
      return addr[TalerFormAttributes.CONTACT_EMAIL];
    case "phone":
      return addr[TalerFormAttributes.CONTACT_PHONE];
    case "postal":
    case "postal-ch":
      return addr[TalerFormAttributes.CONTACT_NAME];
  }
}

export function AnswerChallenge({
  session,
  focus,
  onComplete,
  routeAsk,
}: Props): VNode {
  const { config, lib } = useChallengerApiContext();
  const { i18n } = useTranslationContext();
  const { sent, completed } = useSessionState(session);
  const [pin, setPin] = useState("");
  const [codeError, setCodeError] = useState<TranslatedString>();
  const [codeErrorDetails, setCodeErrorDetails] = useState<string>();
  const [deliveryError, setDeliveryError] = useState<TranslatedString>();
  const [deliveryErrorDetails, setDeliveryErrorDetails] = useState<string>();
  const [newCodeSent, setNewCodeSent] = useState(false);
  const pinInput = useRef<HTMLInputElement | null>(null);
  const { result, retry, isRefreshing } = useChallengeSession(session);

  const lastStatus =
    result && !(result instanceof TalerError) && result.type === "ok"
      ? result.body
      : undefined;
  const retransmissionTime = lastStatus?.retransmission_time.t_s;
  const deadline = useMemo(
    () =>
      retransmissionTime !== undefined
        ? AbsoluteTime.fromProtocolTimestamp({ t_s: retransmissionTime })
        : AbsoluteTime.never(),
    [retransmissionTime],
  );
  const remainingSeconds = useRemainingSeconds(deadline);
  const lastAddress = lastStatus?.last_address
    ? getAddressDescriptionFromAddrType(
        config.address_type,
        lastStatus.last_address,
      )
    : undefined;
  // Translators: Validation error shown when the user enters a one-time code
  // that does not match the current Challenger session.
  const incorrectCodeMessage = i18n.str`The verification code is incorrect.`;
  // Translators: Generic explanation when Challenger rejects the request to
  // load the code-entry step.
  const rejectedRequestMessage = i18n.str`The verification service rejected this request.`;
  // Translators: Loading title or inline status while refreshing the current
  // verification session after a code-related action.
  const updatingStatusLabel = i18n.str`Updating verification status…`;
  // Translators: Page title after the current code has no remaining entry
  // attempts and can no longer be accepted.
  const unusableCodeTitle = i18n.str`This verification code can no longer be used`;
  // Translators: Recovery instruction after all attempts for the current code
  // have been exhausted.
  const exhaustedCodeDescription = i18n.str`Choose another available option or return to the application and start again.`;
  // Translators: Page title above the form where the user enters the one-time
  // verification code they received.
  const enterCodeTitle = i18n.str`Enter your verification code`;
  // Translators: Instruction used when Challenger cannot display the destination
  // to which the one-time verification code was sent.
  const enterReceivedCodeDescription = i18n.str`Enter the one-time verification code you received.`;
  // Translators: Success-notice heading after Challenger has delivered a
  // replacement one-time verification code.
  const newCodeSentTitle = i18n.str`New code sent`;
  // Translators: A successfully delivered replacement code invalidates the old
  // code, so this tells the user to enter the most recently received code.
  const enterLatestCodeDescription = i18n.str`A new code was sent. Enter the latest code you received.`;
  // Translators: Form-field label for the one-time numeric code sent by email,
  // SMS, or postal mail.
  const verificationCodeLabel = i18n.str`Verification code`;
  // Translators: Error-notice heading shown when Challenger rejects an entered
  // verification code.
  const codeNotAcceptedTitle = i18n.str`Code not accepted`;
  // Translators: Error-notice heading after Challenger fails to resend a
  // verification code.
  const codeSendFailureTitle = i18n.str`Code could not be sent`;
  // Translators: Warning shown when only one code-entry attempt remains in the
  // current verification session.
  const lastAttemptWarning = i18n.str`This is your last attempt with the current code.`;
  // Translators: Busy-state label while Challenger checks an entered code.
  const verifyingLabel = i18n.str`Verifying…`;
  // Translators: Primary button that submits the entered one-time code for
  // verification.
  const verifyCodeLabel = i18n.str`Verify code`;

  const resend = useAsyncAction<
    Awaited<ReturnType<typeof lib.challenger.challenge>>,
    [string, Record<string, string>]
  >((ct, nonce, address) => lib.challenger.challenge(nonce, address), {
    onResult(operationResult) {
      if (operationResult.type === "ok") {
        setDeliveryError(undefined);
        setDeliveryErrorDetails(undefined);
        if (operationResult.body.type === "completed") {
          completed(operationResult.body);
          onComplete();
        } else {
          setPin("");
          setCodeError(undefined);
          setCodeErrorDetails(undefined);
          setNewCodeSent(true);
          sent(operationResult.body);
          void revalidateChallengeSession();
        }
        return;
      }
      setNewCodeSent(false);
      setDeliveryError(challengeFailureMessage(i18n, operationResult));
      setDeliveryErrorDetails(formatHttpErrorDetails(operationResult));
    },
    onError(error) {
      setNewCodeSent(false);
      setDeliveryError(
        i18n.str`We could not contact the verification service. Check your connection and try again.`,
      );
      setDeliveryErrorDetails(formatErrorDetails(error));
    },
  });

  const solve = useAsyncAction<
    Awaited<ReturnType<typeof lib.challenger.solve>>,
    [string, Record<string, string>]
  >((ct, nonce, body) => lib.challenger.solve(nonce, body), {
    onResult(operationResult) {
      if (operationResult.type === "ok") {
        if (operationResult.body.type === "completed") {
          setCodeError(undefined);
          setCodeErrorDetails(undefined);
          completed(operationResult.body);
          onComplete();
          return;
        }
        setPin("");
        setCodeError(incorrectCodeMessage);
        setCodeErrorDetails(undefined);
        pinInput.current?.focus();
        void revalidateChallengeSession();
        return;
      }
      const pending = getPendingResponse(operationResult);
      if (pending) {
        setPin("");
        setCodeError(incorrectCodeMessage);
        setCodeErrorDetails(undefined);
        pinInput.current?.focus();
        void revalidateChallengeSession();
        return;
      }
      setCodeError(solveFailureMessage(i18n, operationResult.case));
      setCodeErrorDetails(formatHttpErrorDetails(operationResult));
    },
    onError(error) {
      setCodeError(
        i18n.str`We could not contact the verification service. Check your connection and try again.`,
      );
      setCodeErrorDetails(formatErrorDetails(error));
    },
  });

  useEffect(() => {
    if (newCodeSent && pinInput.current) {
      doAutoFocus(pinInput.current);
    }
  }, [newCodeSent, lastStatus?.auth_attempts_left]);

  if (!result) {
    return <LoadingState title={i18n.str`Loading verification…`} />;
  }
  if (result instanceof TalerError) {
    const technicalDetails = formatErrorDetails(result);
    return (
      <VerificationCard title={i18n.str`Verification service unavailable`}>
        <InlineNotice
          tone="error"
          title={i18n.str`We could not load your verification`}
        >
          <i18n.Translate>
            Check your connection and try again. Your progress is safe.
          </i18n.Translate>
        </InlineNotice>
        <div class="mt-5">
          <ActionButton
            busyLabel={i18n.str`Loading…`}
            onClick={async () => {
              await retry();
            }}
          >
            <i18n.Translate>Try again</i18n.Translate>
          </ActionButton>
        </div>
        <TechnicalDetails copyText={technicalDetails}>
          {technicalDetails}
        </TechnicalDetails>
      </VerificationCard>
    );
  }
  if (result.type === "fail") {
    const technicalDetails = formatHttpErrorDetails(result);
    return (
      <VerificationCard title={i18n.str`Could not load verification details`}>
        <InlineNotice tone="error" title={i18n.str`Verification unavailable`}>
          {result.case === HttpStatusCode.NotFound
            ? i18n.str`This verification has expired. Return to the application and start again.`
            : rejectedRequestMessage}
        </InlineNotice>
        <TechnicalDetails copyText={technicalDetails}>
          {technicalDetails}
        </TechnicalDetails>
      </VerificationCard>
    );
  }
  if (!lastStatus) {
    return <LoadingState title={updatingStatusLabel} />;
  }

  const cantTry = lastStatus.auth_attempts_left === 0;
  const canChange = lastStatus.changes_left > 0;
  const resendState = getCodeResendState(
    !!lastStatus.last_address,
    lastStatus.pin_transmissions_left,
    remainingSeconds,
  );
  const canVerify = pin.length > 0 && !cantTry;
  const onResend = lastStatus.last_address
    ? async () => {
        setNewCodeSent(false);
        await resend.run(session.nonce, lastStatus.last_address!);
      }
    : undefined;

  if (cantTry) {
    return (
      <VerificationCard
        step="code"
        title={unusableCodeTitle}
        description={exhaustedCodeDescription}
      >
        <CodeActions
          mode="exhausted"
          addressType={config.address_type}
          routeAsk={routeAsk.url({})}
          returnUrl={session.redirectURL}
          canChange={canChange}
          resendState={resendState}
          remainingSeconds={remainingSeconds}
          resendRunning={resend.running}
          onResend={onResend}
          deliveryError={deliveryError}
          deliveryErrorDetails={deliveryErrorDetails}
          codeSendFailureTitle={codeSendFailureTitle}
        />
      </VerificationCard>
    );
  }

  return (
    <VerificationCard
      step="code"
      title={enterCodeTitle}
      description={
        lastAddress ? (
          // Translators: %1$s is the email address, phone number, or postal
          // addressee to which the one-time verification code was sent.
          <i18n.Translate>
            We sent a one-time verification code to {lastAddress}.
          </i18n.Translate>
        ) : (
          enterReceivedCodeDescription
        )
      }
    >
      {isRefreshing ? (
        <p
          class="mb-3 text-center text-sm text-secondary dark:text-darkSecondary"
          role="status"
        >
          {updatingStatusLabel}
        </p>
      ) : undefined}

      {newCodeSent ? (
        <InlineNotice tone="success" title={newCodeSentTitle}>
          {enterLatestCodeDescription}
        </InlineNotice>
      ) : undefined}

      <form
        class="mt-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (canVerify) void solve.run(session.nonce, { pin });
        }}
      >
        <label htmlFor="pin" class="block text-sm font-semibold">
          {verificationCodeLabel}
        </label>
        <input
          ref={(element) => {
            pinInput.current = element;
            if (focus) doAutoFocus(element);
          }}
          id="pin"
          name="pin"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={64}
          value={pin}
          placeholder="123456"
          class="mt-2 block w-full rounded-lg border px-3.5 py-2.5 text-base shadow-sm focus:ring-2"
          aria-invalid={!!codeError}
          aria-describedby={codeError ? "verification-code-error" : undefined}
          onInput={(event) => {
            setPin(event.currentTarget.value);
            setCodeError(undefined);
            setCodeErrorDetails(undefined);
          }}
        />

        {codeError ? (
          <div id="verification-code-error" class="mt-4">
            <InlineNotice tone="error" title={codeNotAcceptedTitle}>
              {codeError}
            </InlineNotice>
            {codeErrorDetails ? (
              <TechnicalDetails copyText={codeErrorDetails}>
                {codeErrorDetails}
              </TechnicalDetails>
            ) : undefined}
          </div>
        ) : undefined}

        {lastStatus.auth_attempts_left === 1 ? (
          <p class="mt-3 text-sm font-semibold text-onWarningContainer dark:text-darkWarning">
            {lastAttemptWarning}
          </p>
        ) : undefined}

        <div class="mt-6">
          <ActionButton
            submit
            busyLabel={verifyingLabel}
            running={solve.running}
            disabled={!canVerify}
          >
            {verifyCodeLabel}
          </ActionButton>
        </div>
      </form>

      <CodeActions
        mode="entry"
        addressType={config.address_type}
        routeAsk={routeAsk.url({})}
        canChange={canChange}
        resendState={resendState}
        remainingSeconds={remainingSeconds}
        resendRunning={resend.running}
        onResend={onResend}
        deliveryError={deliveryError}
        deliveryErrorDetails={deliveryErrorDetails}
        codeSendFailureTitle={codeSendFailureTitle}
      />
    </VerificationCard>
  );
}

export type CodeResendState =
  | "ready"
  | "cooldown"
  | "exhausted"
  | "unavailable";

export function getCodeResendState(
  hasAddress: boolean,
  transmissionsLeft: number,
  remainingSeconds: number,
): CodeResendState {
  if (!hasAddress) return "unavailable";
  if (transmissionsLeft <= 0) return "exhausted";
  if (remainingSeconds > 0) return "cooldown";
  return "ready";
}

export type ExhaustedRecoveryAction = "send" | "change" | "return";

export function getExhaustedRecoveryAction(
  resendState: CodeResendState,
  canChange: boolean,
): ExhaustedRecoveryAction {
  if (resendState === "ready" || resendState === "cooldown") return "send";
  if (canChange) return "change";
  return "return";
}

function CodeActions({
  mode,
  addressType,
  routeAsk,
  returnUrl,
  canChange,
  resendState,
  remainingSeconds,
  resendRunning,
  onResend,
  deliveryError,
  deliveryErrorDetails,
  codeSendFailureTitle,
}: {
  mode: "entry" | "exhausted";
  addressType: ChallengerApi.ChallengerTermsOfServiceResponse["address_type"];
  routeAsk: string;
  returnUrl?: string;
  canChange: boolean;
  resendState: CodeResendState;
  remainingSeconds: number;
  resendRunning: boolean;
  onResend?: () => Promise<void>;
  deliveryError?: TranslatedString;
  deliveryErrorDetails?: string;
  codeSendFailureTitle: TranslatedString;
}): VNode {
  const { i18n } = useTranslationContext();
  // Translators: Secondary action that opens a form for replacing the email
  // address that received the current verification code.
  const useDifferentEmail = i18n.str`Use a different email address`;
  // Translators: Secondary action that opens a form for replacing the phone
  // number that received the current verification code.
  const useDifferentPhone = i18n.str`Use a different phone number`;
  // Translators: Secondary action that opens a form for replacing the postal
  // address that received the current verification letter.
  const useDifferentPostal = i18n.str`Use a different postal address`;
  const useDifferentDestination =
    addressType === "email"
      ? useDifferentEmail
      : addressType === "phone"
        ? useDifferentPhone
        : useDifferentPostal;
  // Translators: Explanation shown after the user has used all permitted email
  // address changes for this verification.
  const emailCannotChange = i18n.str`The email address can no longer be changed.`;
  // Translators: Explanation shown after the user has used all permitted phone
  // number changes for this verification.
  const phoneCannotChange = i18n.str`The phone number can no longer be changed.`;
  // Translators: Explanation shown after the user has used all permitted postal
  // address changes for this verification.
  const postalCannotChange = i18n.str`The postal address can no longer be changed.`;
  const destinationCannotChange =
    addressType === "email"
      ? emailCannotChange
      : addressType === "phone"
        ? phoneCannotChange
        : postalCannotChange;
  // Translators: Busy-state label while Challenger sends a replacement one-time
  // code to the same destination.
  const sendingCodeLabel = i18n.str`Sending a new code…`;
  // Translators: Button that sends a replacement one-time code. When delivery
  // succeeds, this new code replaces the previously valid code.
  const sendNewCodeLabel = i18n.str`Send a new code`;
  // Translators: Heading for recovery actions below the normal verification-code
  // form, such as sending another code or changing its destination.
  const didNotReceiveCodeTitle = i18n.str`Didn't receive a code?`;
  // Translators: Heading for recovery actions after the current verification
  // code has no entry attempts remaining.
  const continueVerificationTitle = i18n.str`Continue verification`;
  // Translators: %1$s is a countdown formatted as minutes:seconds, for example
  // 1:05. It tells the user when sending a replacement code becomes available.
  const sendCountdown = i18n.str`You can send a new code in ${formatCountdown(remainingSeconds)}.`;
  // Translators: Explanation shown when the verification has used all permitted
  // one-time-code deliveries.
  const transmissionsExhausted = i18n.str`No more codes can be sent for this verification.`;
  // Translators: Rare recovery-state explanation when Challenger has no email
  // address, phone number, or postal address to which it could send another code.
  const destinationUnavailable = i18n.str`No destination is available for another code.`;
  const canSendEventually =
    resendState === "ready" || resendState === "cooldown";
  const canSendNow = resendState === "ready";
  const exhaustedPrimary = getExhaustedRecoveryAction(resendState, canChange);
  const returnHost = returnUrl ? new URL(returnUrl).host : undefined;
  // Translators: %1$s is the host name of the application that requested the
  // verification. This is the final recovery action when verification cannot
  // continue in Challenger.
  const returnLabel = returnHost
    ? i18n.str`Return to ${returnHost}`
    : undefined;
  const sendButton =
    mode === "exhausted" ? (
      <ActionButton
        busyLabel={sendingCodeLabel}
        running={resendRunning}
        disabled={!canSendNow}
        onClick={canSendNow ? onResend : undefined}
      >
        {sendNewCodeLabel}
      </ActionButton>
    ) : (
      <SecondaryButton
        busyLabel={sendingCodeLabel}
        running={resendRunning}
        disabled={!canSendNow}
        onClick={canSendNow ? onResend : undefined}
      >
        {sendNewCodeLabel}
      </SecondaryButton>
    );

  return (
    <section
      class={`${mode === "entry" ? "mt-7 border-t border-outlineVariant pt-6 dark:border-darkSecondaryContainer" : ""}`}
      aria-labelledby="code-recovery-title"
    >
      <h2 id="code-recovery-title" class="text-base font-bold">
        {mode === "entry" ? didNotReceiveCodeTitle : continueVerificationTitle}
      </h2>

      {deliveryError ? (
        <div class="mt-4">
          <InlineNotice tone="error" title={codeSendFailureTitle}>
            {deliveryError}
          </InlineNotice>
          {deliveryErrorDetails ? (
            <TechnicalDetails copyText={deliveryErrorDetails}>
              {deliveryErrorDetails}
            </TechnicalDetails>
          ) : undefined}
        </div>
      ) : undefined}

      <div class="mt-4 space-y-3">
        {canSendEventually ? sendButton : undefined}
        {resendState === "cooldown" ? (
          <p
            class="text-sm text-secondary dark:text-darkSecondary"
            role="status"
          >
            {sendCountdown}
          </p>
        ) : undefined}
        {resendState === "exhausted" ? (
          <p class="text-sm text-secondary dark:text-darkSecondary">
            {transmissionsExhausted}
          </p>
        ) : undefined}
        {resendState === "unavailable" ? (
          <p class="text-sm text-secondary dark:text-darkSecondary">
            {destinationUnavailable}
          </p>
        ) : undefined}

        {mode === "exhausted" && exhaustedPrimary === "change" ? (
          <PrimaryActionLink href={routeAsk}>
            {useDifferentDestination}
          </PrimaryActionLink>
        ) : canChange ? (
          mode === "exhausted" ? (
            <SecondaryAction href={routeAsk}>
              {useDifferentDestination}
            </SecondaryAction>
          ) : (
            <TextActionLink href={routeAsk}>
              {useDifferentDestination}
            </TextActionLink>
          )
        ) : (
          <p class="text-sm text-secondary dark:text-darkSecondary">
            {destinationCannotChange}
          </p>
        )}

        {mode === "exhausted" &&
        exhaustedPrimary === "return" &&
        returnUrl &&
        returnLabel ? (
          <PrimaryActionLink href={returnUrl}>{returnLabel}</PrimaryActionLink>
        ) : undefined}
      </div>
    </section>
  );
}

function getPendingResponse(
  result: unknown,
): ChallengerApi.InvalidPinResponse | undefined {
  if (!result || typeof result !== "object" || !("body" in result)) {
    return undefined;
  }
  const body = (result as { body?: ChallengerApi.InvalidPinResponse }).body;
  return body?.type === "pending" ? body : undefined;
}

function solveFailureMessage(
  i18n: ReturnType<typeof useTranslationContext>["i18n"],
  status: HttpStatusCode,
): TranslatedString {
  switch (status) {
    case HttpStatusCode.BadRequest:
      // Translators: Validation error when the verification-code field contains
      // characters other than decimal digits.
      return i18n.str`The verification code must contain only numbers.`;
    case HttpStatusCode.NotFound:
      return i18n.str`This verification has expired. Return to the application and start again.`;
    case HttpStatusCode.NotAcceptable:
    case HttpStatusCode.UnsupportedMediaType:
      return i18n.str`The verification page and service are incompatible.`;
    case HttpStatusCode.PayloadTooLarge:
      // Translators: Validation error when the entered one-time code exceeds the
      // maximum length accepted by Challenger.
      return i18n.str`The verification code is too long.`;
    case HttpStatusCode.InternalServerError:
      return i18n.str`The verification service had a temporary problem. Try again.`;
    default:
      // Translators: Generic retry instruction when Challenger could not check
      // the entered one-time code for an unspecified reason.
      return i18n.str`The verification code could not be checked. Try again.`;
  }
}

function useRemainingSeconds(deadline: AbsoluteTime): number {
  const [remaining, setRemaining] = useState(() =>
    calculateRemainingSeconds(deadline),
  );
  useEffect(() => {
    setRemaining(calculateRemainingSeconds(deadline));
    if (AbsoluteTime.isNever(deadline) || AbsoluteTime.isExpired(deadline)) {
      return;
    }
    const timer = setInterval(
      () => setRemaining(calculateRemainingSeconds(deadline)),
      1000,
    );
    return () => clearInterval(timer);
  }, [deadline]);
  return remaining;
}

function calculateRemainingSeconds(deadline: AbsoluteTime): number {
  const remaining = AbsoluteTime.remaining(deadline).d_ms;
  return remaining === "forever" ? 0 : Math.ceil(remaining / 1000);
}

export function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function doAutoFocus(element: HTMLElement | null): void {
  if (!element) return;
  setTimeout(() => element.focus({ preventScroll: true }), 100);
}

export function undefinedIfEmpty<T extends object>(obj: T): T | undefined {
  return Object.keys(obj).some(
    (key) => (obj as Record<string, T>)[key] !== undefined,
  )
    ? obj
    : undefined;
}
